import { Journey } from '@audibene/ta.utils-opportunity'
import { differenceInBusinessDays, differenceInCalendarDays } from 'date-fns'
import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import {
  AcfuProgressiveDisclosureParameters,
  ANY_RESOURCE_ID,
  AppointmentRescheduleReason,
  AppointmentStatus,
  AppointmentType,
  Country,
  EventType,
  Route,
  RouteParameters,
  TeamAccess,
} from '../../../constants'
import {
  useAcfuActingUser,
  useAppointmentTypeOptions,
  useAuth,
  useBlockedAppointmentType,
  useCountry,
  useCustomer,
  useEventTracking,
  useFeatureFlags,
  useFormAnalytics,
  useIsSchedulingV2RedirectPending,
  useIsManualMode,
  useNotification,
  useNow,
  useRecommendedAvailabilitiesHookName,
  useRescheduleContext,
  useSearchParameters,
  useShop,
  useShouldRestrictToHAE,
  useTimeZones,
  useTrackError,
} from '../../../hooks'
import { useAppointmentsService } from '../../../hooks/services'
import { useAppointments } from '../../../hooks/useAppointments'
import { parseAppointmentType } from '../../../hooks/useScheduleFormDefaultValues'
import { getOfferedSubstituteAppointmentType } from '../../../hooks/useTrialGate/trialGates'
import {
  Appointment,
  AppointmentCreateRequest,
  AppointmentFormData,
  AppointmentRescheduleFormData,
  AppointmentRescheduleRequest,
  EventData,
} from '../../../types'
import { hydratePathname, isSlotUnavailableError, logFactory, selectRandomItem } from '../../../util'
import { handleSubmissionFailure, trackAcfuBookingOutcome } from '../../Appointment/util'
import { Issues } from '../../Issues'
import { Loading } from '../../Reservation/Common/Loading'
import { Form } from '../../forms'
import { AppointmentTypeSelection } from '../AppointmentTypeSelection'
import { validationSchema } from './validationSchema'
import { ScheduleForm } from './Form'
import { QueryRefreshTimer } from './QueryRefreshTimer'

const log = logFactory('AppointmentForm')

/**
 * Initial page to create an appointment with provider recommendationappointmentType
 */

const New = () => {
  // Query parameters
  const navigate = useNavigate()
  const searchParams = useSearchParameters()
  const journey = searchParams.journey as string | undefined

  const opportunityGids: string[] | undefined =
    typeof searchParams.opportunity_gid === 'string' ? [searchParams.opportunity_gid] : searchParams.opportunity_gid

  const defaultAppointmentType = searchParams.appointment_type as AppointmentType | undefined

  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(rescheduleContext?.reason)
  const client = useQueryClient()
  const trackError = useTrackError()

  const { user, isLoading: isUserLoading } = useAuth()

  const { customer, isLoading: isCustomerLoading } = useCustomer()
  const { shop } = useShop()
  // This route builds its own form defaults rather than going through useScheduleFormDefaultValues,
  // so the trial gate has to be applied here too. Without it the URL's type stays the form's value
  // even when the dropdown renders it disabled, and the booking goes through on submit - the block
  // would be visual only (PX-1807/PX-3749).
  const { blockedAppointmentTypes, isLoading: isTrialGateLoading } = useBlockedAppointmentType()
  const { timeZones = {}, isLoading: isTimeZonesLoading } = useTimeZones()
  const { track: trackEvent } = useEventTracking()
  const { notify } = useNotification()
  const country = useCountry()
  // The same query ScheduleForm runs, so react-query dedupes it to no extra request and both agree
  // on what this shop offers.
  const { appointmentTypes: configuredAppointmentTypes, isLoading: areAppointmentTypesLoading } = useAppointmentTypeOptions({
    country: country as Country,
    journey: journey as Journey,
  })
  const { createAppointment, rescheduleAppointment } = useAppointmentsService()
  const { isManualMode } = useIsManualMode()
  const { readSummary } = useFormAnalytics<AppointmentFormData>()

  const { data: now = new Date() } = useNow()
  const shouldRestrictToHAE = useShouldRestrictToHAE(journey)
  const isV2RedirectPending = useIsSchedulingV2RedirectPending()
  const [
    isAppointmentTypeSelectionEnabled,
    isAftercareFollowUpRecommendationsEnabled,
    isAvailabilityCacheEnabled,
    isAcfuProgressiveDisclosureEnabled,
  ] = useFeatureFlags<boolean>([
    'ENABLE_APPOINTMENT_TYPE_SELECTION',
    'ENABLE_AFTERCARE_FOLLOW_UP_RECOMMENDATIONS',
    'ENABLE_AVAILABILITY_CACHE',
    'ACFU_PROGRESSIVE_DATE_DISCLOSURE',
  ])
  const { actingUserId, actingUserRole } = useAcfuActingUser()
  const [appointmentTypeSelection, onAppointmentTypeSelected] = useState<AppointmentType | undefined>(undefined)

  // If consultant restriction applies and default type is not HAE, force HAE
  const effectiveDefaultAppointmentType =
    shouldRestrictToHAE && defaultAppointmentType !== AppointmentType.HEARING_AID_EVALUATION
      ? AppointmentType.HEARING_AID_EVALUATION
      : defaultAppointmentType

  const [appointmentType, setAppointmentType] = useState(effectiveDefaultAppointmentType)
  const trackingData = useMemo(
    () => ({
      userId: user?.account_id,
      customerGid: searchParams.customer_gid as string,
      type: appointmentType,
    }),
    [user, searchParams.customer_gid, appointmentType]
  )

  const { data: completedOrConfirmedHearingAidEvaluationAppointments = [], isLoading: areAppointmentsLoading } = useAppointments(
    {
      opportunity_gid: opportunityGids,
      sort_by: 'start_date',
      sort: 'desc',
      status: [AppointmentStatus.CONFIRMED, AppointmentStatus.COMPLETED],
      type: [AppointmentType.HEARING_AID_EVALUATION],
    },
    Boolean(opportunityGids?.length && isAppointmentTypeSelectionEnabled)
  )

  // Check if required query parameters are present
  const issuesSummary: ComponentProps<typeof Issues>['summary'] = {}

  // Loading data
  // The trial gate is part of this: the default type below is chosen once, so building it before the
  // gate settles would hand the form a type the rule goes on to block. So is the appointment-type
  // query, which decides whether the replacement is one this shop offers - without it the default
  // lands on the blocked type and ScheduleForm's effect corrects it a commit later, clearing the
  // date and time with it.
  //
  // Only when that query is enabled: useAppointmentTypeOptions reports a disabled one (no
  // country/journey) as perpetually loading, and those are exactly the cases the Issues screen below
  // exists to report - gating on it unconditionally would replace that screen with a spinner that
  // never resolves.
  const areAppointmentTypesPending = Boolean(country && journey) && areAppointmentTypesLoading
  const isLoading =
    isTimeZonesLoading || isCustomerLoading || areAppointmentsLoading || isUserLoading || isTrialGateLoading || areAppointmentTypesPending

  // Sticky, because onSubmitClick clears the reschedule context on success and only then navigates:
  // reading isRescheduling live below would let the selection screen replace the form mid-submit.
  // Mount-scoped, which is sound only because New remounts per route: the one in-mount clearContext
  // is followed straight away by a navigate to a different route.
  const [isReschedulingSession, setIsReschedulingSession] = useState<boolean>(false)
  useEffect(() => {
    if (isRescheduling) {
      setIsReschedulingSession(true)
    }
  }, [isRescheduling])

  // Derived during render rather than mirrored into state by an effect: a state flip only lands on
  // the commit after the one that learns the customer's appointment history, and rendering
  // ScheduleForm in that gap made it report a trial-gate impression for a surface the provider never
  // saw. Both reschedule terms are load-bearing: the live one covers the first commit, before the
  // sticky one has been set, and the sticky one covers every commit after the context is cleared.
  const isAppointmentTypeSelectionRequired =
    isAppointmentTypeSelectionEnabled &&
    !isRescheduling &&
    !isReschedulingSession &&
    !appointmentTypeSelection &&
    completedOrConfirmedHearingAidEvaluationAppointments.length > 0

  // The form reads its defaults from appointmentType, which the effect below only catches up to on
  // the commit after a type is picked. Holding the selection screen until it has caught up is what
  // keeps the form from mounting on the type the page was opened with, and it closes the same gap
  // for a click landing before that effect has flushed. Gated on the flag as well, because the
  // effect gives up when the flag is off: without that term, a flag streaming off between the click
  // and the effect would pin the provider on the selection screen for a feature that is disabled.
  const isSelectedAppointmentTypePending =
    isAppointmentTypeSelectionEnabled && Boolean(appointmentTypeSelection) && appointmentTypeSelection !== appointmentType

  useEffect(() => {
    if (!isAppointmentTypeSelectionEnabled) {
      return
    }
    if (isRescheduling) {
      return
    }
    if (appointmentTypeSelection) {
      setAppointmentType(appointmentTypeSelection)
      trackEvent(EventType.INTERMEDIATE_APPOINTMENT_TYPE_SELECTION_PAGE_BUTTON_CLICKED, {
        data: {
          appointmentTypeSelected: appointmentType as AppointmentType,
          appointmentTypeURLParam: defaultAppointmentType as AppointmentType,
        },
      })

      if (
        appointmentTypeSelection === AppointmentType.AFTERCARE_FOLLOW_UP &&
        !isAftercareFollowUpRecommendationsEnabled &&
        !isV2RedirectPending
      ) {
        const pathname = hydratePathname(Route.CONSULTANT_SCHEDULE, {
          [RouteParameters.COUNTRY]: country,
        })
        const url = new URL(window.location.href)
        url.pathname = pathname
        url.searchParams.set('appointment_type', AppointmentType.AFTERCARE_FOLLOW_UP)
        navigate(`${url.pathname}${url.search}`)
      }
    }
  }, [appointmentTypeSelection, country, isV2RedirectPending])

  if (isLoading) {
    return <Loading />
  }
  if (!appointmentType) issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.appointment_type']
  if (!journey) issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.journey']
  if (!opportunityGids?.length)
    issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.opportunity_gid']

  if (Object.keys(issuesSummary).length) {
    return <Issues summary={issuesSummary} />
  }

  // Check if customer is valid
  if (!customer) {
    return (
      <Issues summary={{ 'customer.notFound': [] }}>
        <QueryRefreshTimer />
      </Issues>
    )
  }

  if (isAppointmentTypeSelectionRequired || isSelectedAppointmentTypePending) {
    return <AppointmentTypeSelection onAppointmentTypeSelected={onAppointmentTypeSelected} />
  }

  // Steer away from a type the customer's trial status blocks, exactly as useScheduleFormDefaultValues
  // does for the routes that use it - never while rescheduling, where the type is the existing
  // appointment's own and substituting would silently reclassify it.
  //
  // Through parseAppointmentType, like that hook and the substitution alert: appointmentType is the
  // raw search parameter, so an unrecognized casing would otherwise miss the blocked-set test, skip
  // substitution, and reach the form as a value no option matches.
  const requestedFormAppointmentType = shouldRestrictToHAE ? AppointmentType.HEARING_AID_EVALUATION : parseAppointmentType(appointmentType)
  const substitutedFormAppointmentType = isReschedulingSession
    ? undefined
    : getOfferedSubstituteAppointmentType(requestedFormAppointmentType, blockedAppointmentTypes, configuredAppointmentTypes)
  const defaultFormAppointmentType = substitutedFormAppointmentType ?? requestedFormAppointmentType

  const defaultAppointmentFormValues: AppointmentFormData = {
    // '' not null: this flow doesn't route defaults through toSafeInputValues (unlike
    // useScheduleFormDefaultValues), so a null default here triggers React's controlled/
    // uncontrolled textarea warning once AcfuExtendedWindowReason renders.
    acfuExtendedWindowReason: '',
    acfuRequiresExtendedWindowReason: false,
    acfuShowMoreClickCount: 0,
    acfuVisibleWindowDays: AcfuProgressiveDisclosureParameters.DEFAULT_WINDOW_IN_DAYS,
    clientStartDate: now,
    clientStartTime: now,
    isManualMode: false,
    country: customer.countryCode,
    customer_salesforce_id: customer.salesforceId,
    customer_timezone: customer.timezone as string,
    journey: journey as Journey,
    opportunity_gid: opportunityGids?.[0] ?? '',
    resource_ids: null,
    specialist_id: ANY_RESOURCE_ID,
    month_date: now,
    start_date: now,
    state: customer.shippingAddress?.state,
    title: null,
    type: defaultFormAppointmentType,
    teamAccess: country === Country.DE ? TeamAccess.MY_TEAM : undefined,
  }

  const onSubmitClick = async (data: AppointmentFormData) => {
    log('📆 Transforming form data...', data)

    const type = data.type as AppointmentType
    const country = data.country as Country
    const journey = data.journey as Journey
    const scheduledCustomerTimezone = data?.customer_timezone
    const startDate = data.start_date as Date

    // Determine resource IDs based on combined booking or single resource
    let resource_ids: string[]

    if (data.isCombinedBooking && data.eligibleResourcePairs?.length) {
      // Combined booking: eligibleResourcePairs contains [fitterId, cabinId?] pairs
      // Pick one randomly
      const selectedPair = selectRandomItem(data.eligibleResourcePairs)
      resource_ids = selectedPair.filter(Boolean) as string[]

      log('✅ Creating combined booking', {
        selectedPair,
        resource_ids,
      })
    } else {
      // Single resource booking (existing behavior)
      resource_ids = data?.resource_ids as string[]

      log('ℹ️ Creating employee-only booking', {
        employees: resource_ids,
      })
    }

    // Get analytics data
    const { didError, errorCounts, totalClicks, totalSessionDurationInSeconds, submitCount } = readSummary()

    const request: AppointmentCreateRequest = {
      country,
      customer_salesforce_id: data?.customer_salesforce_id as string,
      duration_in_minutes: data?.duration_in_minutes as number,
      is_manual: isManualMode,
      journey,
      opportunity_gid: data?.opportunity_gid as string,
      resource_ids,
      scheduled_customer_timezone: scheduledCustomerTimezone as string,
      start_date: startDate,
      title: data.title as string,
      type,
    }

    const eventData: EventData = {
      ...trackingData,
      customerDefaultTimeZone: data.customer_timezone as string,
      customerTimeZone: data.customer_timezone as string,
      daysInTheFuture: differenceInCalendarDays(startDate, now),
      daysInTheFutureBusiness: differenceInBusinessDays(startDate, now),
      didError,
      durationDefaultInMinutes: data?.duration_in_minutes as number,
      durationInMinutes: data?.duration_in_minutes as number,
      errorCountResourceNotSelected: errorCounts['resource_ids'] ?? 0,
      errorCountStartDayNotSelected: errorCounts['clientStartDate'] ?? 0,
      errorCountTimePickerInvalid: errorCounts['clientStartTime'] ?? 0,
      formType: 'initial',
      isInitialValid: true,
      isManual: isManualMode ?? false,
      journey: journey as string,
      resourceIds: data?.resource_ids as string[],
      resourceTimeZone: data?.fitter_timezone as string,
      startDate: startDate.toISOString(),
      startDay: startDate.getDate(),
      startDayOfWeek: startDate.getDay(),
      startTimeHour: Number(
        startDate.toLocaleTimeString('en', {
          timeZone: data.customer_timezone as string,
          hour: 'numeric',
          hour12: false,
        })
      ),
      startTimeMinute: Number(
        startDate.toLocaleTimeString('en', {
          timeZone: data.customer_timezone as string,
          minute: 'numeric',
          hour12: false,
        })
      ),
      submissionCount: submitCount + 1,
      totalClicks,
      totalSessionDurationInSeconds,
      type: type as string,
      country: country,
      customerGid: searchParams.customer_gid as string,
      opportunityGid: searchParams.opportunity_gid as string,
      shopGid: (searchParams.shop_gid ?? shop?.gid) as string,
    }

    log('📩 Submitting appointment...', request)

    try {
      let newAppointment: Appointment

      const { description, reason } = rescheduleContext ?? ({} as Partial<AppointmentRescheduleFormData>)

      if (reason) {
        const rescheduleRequest: AppointmentRescheduleRequest = {
          appointmentIdRescheduled: searchParams.appointment_id as string,
          newAppointmentRequest: request,
          reason: reason as AppointmentRescheduleReason,
          description: description ?? undefined,
        }

        newAppointment = await rescheduleAppointment(rescheduleRequest)
        rescheduleContext.clearContext()
      } else {
        newAppointment = await createAppointment(request)
      }

      trackEvent(EventType.APPOINTMENT_FORM_SUBMISSION_SUCCEEDED, {
        data: eventData,
      })

      // This handler serves both create and reschedule (branching on rescheduleContext.reason
      // above) — isRescheduling must be threaded through so a reschedule never fires ACFU analytics.
      trackAcfuBookingOutcome({
        actingUserId,
        actingUserRole,
        data,
        eventData,
        isAcfuProgressiveDisclosureEnabled,
        isRescheduling,
        trackEvent,
      })

      client.invalidateQueries([useRecommendedAvailabilitiesHookName])

      const successRoute = reason ? Route.CONSULTANT_APPOINTMENT_RESCHEDULE_SUCCESS : Route.CONSULTANT_APPOINTMENT_SCHEDULE_SUCCESS
      const pathname = hydratePathname(successRoute, {
        [RouteParameters.COUNTRY]: newAppointment.country,
        [RouteParameters.APPOINTMENT_ID]: newAppointment.id,
      })

      navigate(pathname, { replace: true })
    } catch (error: any) {
      const failedEventData = {
        data: eventData,
        type: EventType.APPOINTMENT_FORM_SUBMISSION_FAILED,
      }
      const invalidateQueries = () => client.invalidateQueries([useRecommendedAvailabilitiesHookName])

      handleSubmissionFailure({
        error,
        eventData: failedEventData,
        invalidateQueries,
        notify,
        trackError,
        trackEvent,
      })

      if (isSlotUnavailableError(error)) {
        trackEvent(EventType.APPOINTMENT_FORM_SUBMISSION_FAILED_SLOT_UNAVAILABLE, {
          data: { ...eventData, wasAvailabilityCacheEnabled: isAvailabilityCacheEnabled },
        })
      }

      // Rethrow so our form's isSubmitSuccessful isn't set to true
      throw error
    }
  }

  return (
    <Form<AppointmentFormData> defaultValues={defaultAppointmentFormValues} onSubmit={onSubmitClick} validationSchema={validationSchema}>
      <ScheduleForm
        customer={customer}
        journey={journey as Journey}
        timeZones={timeZones}
        trackingData={trackingData}
        appointmentTypeSubstitution={
          substitutedFormAppointmentType ? { from: requestedFormAppointmentType, to: substitutedFormAppointmentType } : undefined
        }
      />
    </Form>
  )
}

export { New as ScheduleAppointment }
